
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBuilding,
  FaCheckCircle,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaStore,
  FaUserTie,
} from "react-icons/fa";
import { Chart, registerables } from "chart.js";
import "../styles/Dashboard.css";
import {
  getOrgDashboardSummary,
  getOrgTransactionTrend,
  getOrgMerchantGrowth,
  getOrgQrUsageSplit,
  getOrgTopAgents,
  getOrgTopMerchants,
  getOrgDashboardRecentTransactions,
  getOrgDashboardApprovals,
  getOrgAgentActivity,
  getOrgMonthlyRevenueHeatmap,
} from "../api/api";
import { getOrgId, getUserData } from "../utils/authStorage";

Chart.register(...registerables);

const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);
const pick = (res) => res?.data ?? res ?? null;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const asArray = (raw) =>
  (Array.isArray(raw) && raw) ||
  (Array.isArray(raw?.data) && raw.data) ||
  (Array.isArray(raw?.series) && raw.series) ||
  (Array.isArray(raw?.items) && raw.items) ||
  [];

const formatRs = (v) => {
  const n = num(v);
  if (n >= 1000000) return `Rs ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `Rs ${(n / 1000).toFixed(1)}k`;
  return `Rs ${n.toLocaleString()}`;
};

const initialsOf = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "A";

/* raw series -> [{ label, count, amount }] using whatever labels the API returns */
const toSeries = (raw) =>
  asArray(raw)
    .map((d) => ({
      label: String(d?.label ?? d?.day ?? d?.date ?? d?.month ?? d?.name ?? d?.period ?? ""),
      count: num(d?.count ?? d?.transactions ?? d?.totalTransactions ?? d?.value ?? d?.merchants ?? d?.newMerchants ?? d?.total),
      amount: num(d?.amount ?? d?.volume ?? d?.totalAmount ?? d?.revenue),
    }))
    .filter((d) => d.label);

/* ── dark-mode hook — follows the portal's own theme toggle (body.theme-light) ── */
function useDark() {
  const [dark, setDark] = useState(!document.body.classList.contains("theme-light"));
  useEffect(() => {
    const update = () => setDark(!document.body.classList.contains("theme-light"));
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/* ── generic Chart.js wrapper ── */
function CJSChart({ id, build }) {
  const ref = useRef(null);
  const inst = useRef(null);
  const dark = useDark();

  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = build(ref.current, dark);
    return () => inst.current?.destroy();
  }, [dark, build]);

  return (
    <div style={{ position: "relative", width: "100%", height: 150 }}>
      <canvas ref={ref} id={id} />
    </div>
  );
}

/* ── stat card ── */
const StatTile = ({ label, value, icon, accent, trend, progress }) => (
  <div className="msd-kpi">
    <div className="msd-kpi__head">
      <div className="msd-kpi__label">{label}</div>
      <div className="msd-kpi__icon" style={{ background: accent + "25", color: accent }}>
        {icon}
      </div>
    </div>
    <div className="msd-kpi__value">{value?.toLocaleString?.() ?? value ?? 0}</div>
    {trend ? <div className="msd-kpi__trend">{trend}</div> : null}
    {typeof progress === "number" ? (
      <div className="msd-kpi__progress">
        <div className="msd-kpi__progress-bar" style={{ width: `${progress}%`, background: accent }} />
      </div>
    ) : null}
    <div className="msd-kpi__spark" style={{ background: accent }} />
  </div>
);

const ACTIVITY_DOTS = ["#44edcc", "#a78bfa", "rgba(255,255,255,0.55)"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ══════════════════════════════════════════ */
const OrganizationDashboard = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [qrSplit, setQrSplit] = useState({ dynamicShare: 0, staticShare: 0, dynamicAmount: 0, staticAmount: 0 });
  const [trendSeries, setTrendSeries] = useState([]);
  const [growthSeries, setGrowthSeries] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [topMerchants, setTopMerchants] = useState([]);
  const [recentTx, setRecentTx] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [agentActivity, setAgentActivity] = useState([]);
  const [heatmap, setHeatmap] = useState([]);

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const orgId = getOrgId() || user?.profile?.orgId || user?.orgId || user?.organizationId || null;
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      const results = await Promise.allSettled([
        getOrgDashboardSummary(orgId),
        getOrgTransactionTrend(orgId, "week"),
        getOrgMerchantGrowth(orgId, "week"),
        getOrgQrUsageSplit(orgId),
        getOrgTopAgents(orgId, 5),
        getOrgTopMerchants(orgId, 5),
        getOrgDashboardRecentTransactions(orgId, 10),
        getOrgDashboardApprovals(orgId, 5),
        getOrgAgentActivity(orgId, 10),
        getOrgMonthlyRevenueHeatmap(orgId, year),
      ]);
      if (cancelled) return;

      const val = (i) => (results[i].status === "fulfilled" ? pick(results[i].value) : null);

      // Summary
      setSummary(val(0));

      // Trend + growth charts
      setTrendSeries(toSeries(val(1)));
      setGrowthSeries(toSeries(val(2)));

      // QR usage split — API shape: { dynamicQr: { count, volume, percentage }, staticQr: {...} }
      const qr = val(3) || {};
      const dq = qr.dynamicQr ?? qr.dynamic ?? {};
      const sq = qr.staticQr ?? qr.static ?? {};
      setQrSplit({
        dynamicShare: num(qr.dynamicShare ?? qr.dynamicPercent ?? qr.dynamicQrShare ?? dq.percentage ?? dq.share ?? dq.percent),
        staticShare: num(qr.staticShare ?? qr.staticPercent ?? qr.staticQrShare ?? sq.percentage ?? sq.share ?? sq.percent),
        dynamicAmount: num(qr.dynamicAmount ?? qr.dynamicVolume ?? dq.volume ?? dq.amount),
        staticAmount: num(qr.staticAmount ?? qr.staticVolume ?? sq.volume ?? sq.amount),
      });

      // Top agents
      setTopAgents(
        asArray(val(4)).map((a) => ({
          name: a.name ?? a.agentName ?? "—",
          initials: initialsOf(a.name ?? a.agentName),
          sub: a.region ?? a.sub ?? a.area ?? "—",
          value: formatRs(a.volume ?? a.amount ?? a.totalVolume ?? a.revenue),
          delta: a.delta ?? a.growth ?? a.change ?? "",
        }))
      );

      // Top merchants
      setTopMerchants(
        asArray(val(5)).map((m) => ({
          name: m.name ?? m.merchantName ?? "—",
          sub: m.tier ?? m.sub ?? m.category ?? m.categoryName ?? "—",
          value: formatRs(m.volume ?? m.amount ?? m.totalVolume ?? m.revenue),
          foot: m.status ?? m.foot ?? m.velocity ?? "",
        }))
      );

      // Recent transactions
      setRecentTx(
        asArray(val(6)).map((t) => ({
          time: t.time ?? t.timestamp ?? t.createdAt ?? "—",
          merchant: t.merchant ?? t.merchantName ?? "—",
          method: t.method ?? t.qrType ?? t.paymentMethod ?? "—",
          amount: formatRs(t.amount ?? t.volume),
          status: String(t.status ?? "").toUpperCase() || "PENDING",
        }))
      );

      // Approvals
      setApprovals(
        asArray(val(7)).map((a) => ({
          name: a.name ?? a.title ?? a.merchantName ?? a.agentName ?? "—",
          sub: a.sub ?? a.description ?? a.type ?? a.detail ?? "",
        }))
      );

      // Agent activity
      setAgentActivity(
        asArray(val(8)).map((a, idx) => ({
          dot: ACTIVITY_DOTS[idx % ACTIVITY_DOTS.length],
          name: a.name ?? a.agentName ?? "System",
          sub: a.description ?? a.sub ?? a.action ?? a.detail ?? "",
        }))
      );

      // Monthly revenue heatmap
      const hmRaw = asArray(val(9));
      const hm = hmRaw.length
        ? hmRaw.map((m, idx) => ({
            label: String(m?.label ?? m?.month ?? MONTHS[idx % 12]),
            amount: num(m?.amount ?? m?.revenue ?? m?.volume ?? m?.value),
          }))
        : MONTHS.map((label) => ({ label, amount: 0 }));
      const maxHm = Math.max(...hm.map((m) => m.amount), 0);
      setHeatmap(hm.map((m) => ({ label: m.label, v: maxHm ? (m.amount / maxHm) * 0.75 : 0 })));

      setLoading(false);
    };

    fetchAll().catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [orgId, year]);

  /* Derived chart data */
  const trendLabels = trendSeries.map((d) => d.label);
  const trendCounts = trendSeries.map((d) => d.count);
  const trendAmounts = trendSeries.map((d) => d.amount);
  const growthLabels = growthSeries.map((d) => d.label);
  const growthCounts = growthSeries.map((d) => d.count);

  const totalAmount = sum(trendAmounts);
  const dynamicShare = qrSplit.dynamicShare;
  const staticShare = qrSplit.staticShare;

  const s = summary || {};
  const totalMerchants = num(s.totalMerchants ?? s.merchants ?? s.totalMerchant);
  const totalAgents = num(s.totalAgents ?? s.agents ?? s.totalAgent);
  const successRate = num(s.successRate ?? s.success_rate ?? s.successPercent);
  const pending = num(s.pending ?? s.pendingApprovals ?? s.pendingMerchants);
  const volume = num(s.volume ?? s.totalVolume ?? s.transactionVolume) || totalAmount;
  const revenue = num(s.revenue ?? s.totalRevenue ?? s.netRevenue) || Math.round(totalAmount * 0.038);

  const kpis = [
    { label: "Total Merchants", value: totalMerchants, icon: <FaStore />, accent: "#10B981", trend: "0%" },
    { label: "Total Agents", value: totalAgents, icon: <FaUserTie />, accent: "#B6C4FF", trend: "0%" },
    { label: "Volume", value: formatRs(volume), icon: <FaExchangeAlt />, accent: "#0057FF", trend: "0%" },
    { label: "Revenue", value: formatRs(revenue), icon: <FaMoneyBillWave />, accent: "#FBBF24", trend: "0%" },
    { label: "Success Rate", value: `${successRate}%`, icon: <FaCheckCircle />, accent: "#10B981", progress: Math.max(0, Math.min(100, successRate)) },
    { label: "Pending", value: pending, icon: <FaBuilding />, accent: "#94A3B8", trend: "Awaiting review" },
  ];

  /* Fallback axes so the charts keep their shape when the API returns no data */
  const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const safeTrendLabels = trendLabels.length ? trendLabels : WEEK_LABELS;
  const safeTrendCounts = trendCounts.length ? trendCounts : WEEK_LABELS.map(() => 0);
  const safeGrowthLabels = growthLabels.length ? growthLabels : WEEK_LABELS;
  const safeGrowthCounts = growthCounts.length ? growthCounts : WEEK_LABELS.map(() => 0);
  const qrEmpty = !num(dynamicShare) && !num(staticShare);

  const buildTrend = useMemo(
    () => (canvas, dark) => {
      const grid = dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)";
      const tick = dark ? "rgba(255,255,255,.42)" : "#888";
      return new Chart(canvas, {
        type: "line",
        data: {
          labels: safeTrendLabels,
          datasets: [
            {
              data: safeTrendCounts,
              borderColor: "#0057FF",
              borderWidth: 3,
              pointRadius: 0,
              pointHoverRadius: 5,
              fill: true,
              backgroundColor: (ctx) => {
                const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 220);
                g.addColorStop(0, "rgba(0,87,255,0.22)");
                g.addColorStop(1, "rgba(0,87,255,0.00)");
                return g;
              },
              tension: 0.45,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { color: tick, font: { size: 10 } } },
            y: {
              beginAtZero: true,
              suggestedMax: 1,
              grid: { color: grid },
              ticks: { color: tick, font: { size: 10 }, precision: 0 },
            },
          },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trendSeries]
  );

  const buildGrowth = useMemo(
    () => (canvas, dark) => {
      const tick = dark ? "rgba(255,255,255,.42)" : "#888";
      const maxVal = Math.max(...safeGrowthCounts.map((v) => Number(v) || 0), 0);
      const hasData = maxVal > 0;
      const maxIndex = safeGrowthCounts.findIndex((v) => Number(v) === maxVal);
      const idleBar = dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.12)";
      /* like the Figma mock: faint skeleton bars while there is no data yet */
      const displayCounts = hasData ? safeGrowthCounts : [3, 5, 4, 7, 4, 6, 3].slice(0, safeGrowthLabels.length);
      const colors = displayCounts.map((v, i) => (hasData && i === maxIndex ? "#44EDCC" : idleBar));

      /* "+N" pill above the highlighted bar */
      const peakLabel = {
        id: "growthPeakLabel",
        afterDatasetsDraw(chart) {
          if (!hasData) return;
          const bar = chart.getDatasetMeta(0)?.data?.[maxIndex];
          if (!bar) return;
          const { x, y } = bar.tooltipPosition();
          const ctx = chart.ctx;
          const text = `+${maxVal}`;
          ctx.save();
          ctx.font = "700 10px sans-serif";
          const w = ctx.measureText(text).width + 14;
          ctx.fillStyle = dark ? "rgba(10, 13, 25, 0.95)" : "#0f172a";
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(x - w / 2, y - 26, w, 18, 6);
          else ctx.rect(x - w / 2, y - 26, w, 18);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(text, x, y - 17);
          ctx.restore();
        },
      };

      return new Chart(canvas, {
        type: "bar",
        data: {
          labels: safeGrowthLabels,
          datasets: [
            {
              data: displayCounts,
              backgroundColor: colors,
              borderRadius: 10,
              borderSkipped: false,
              barPercentage: 0.65,
              categoryPercentage: 0.7,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 26 } },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: hasData },
          },
          scales: {
            x: { grid: { display: false }, ticks: { color: tick, font: { size: 10 } } },
            y: { grid: { display: false }, ticks: { display: false } },
          },
        },
        plugins: [peakLabel],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [growthSeries]
  );

  /* Same behaviour as the super-admin QR Adoption doughnut: grey ring when
     empty, tooltip always shows the real amount/share (never the placeholder 1) */
  const buildQrSplit = useMemo(
    () => (canvas, dark) => {
      const border = dark ? "rgba(5,8,22,0.92)" : "#fff";
      const placeholder = dark ? "rgba(255,255,255,0.10)" : "#EEEDFE";
      const labels = ["Dynamic QR", "Static QR"];
      const shares = [dynamicShare, staticShare];
      const amounts = [qrSplit.dynamicAmount, qrSplit.staticAmount];
      return new Chart(canvas, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: qrEmpty ? [1, 1] : shares,
              backgroundColor: qrEmpty ? [placeholder, placeholder] : ["#534AB7", "#1D9E75"],
              borderColor: border,
              borderWidth: 1.5,
              hoverOffset: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "68%",
          animation: { duration: 700, animateRotate: true },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const share = qrEmpty ? 0 : num(shares[ctx.dataIndex]);
                  const amount = num(amounts[ctx.dataIndex]);
                  return ` ${formatRs(amount)} (${share}%)`;
                },
              },
            },
          },
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicShare, staticShare, qrSplit.dynamicAmount, qrSplit.staticAmount, qrEmpty]
  );

  if (loading) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <p className="msd-muted">Loading dashboard…</p>
      </div>
    );
  }

  return (
    <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="msd-kpis msd-kpis--admin">
        {kpis.map((t) => (
          <StatTile
            key={t.label}
            label={t.label}
            value={t.value}
            icon={t.icon}
            accent={t.accent}
            trend={t.trend}
            progress={t.progress}
          />
        ))}
      </div>

      <div className="msd-admingrid">
        <div className="msd-admingrid__main">
          <div className="msd-admingrid__row3">
            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Transaction Trend</div>
                  <div className="msd-card__subtitle">Real-time throughput</div>
                </div>
              </div>
              <div className="msd-card__body">
                <CJSChart id="org-trend" build={buildTrend} />
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Merchant Growth</div>
                  <div className="msd-card__subtitle">New onboardings</div>
                </div>
              </div>
              <div className="msd-card__body">
                <CJSChart id="org-growth" build={buildGrowth} />
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">QR Adoption</div>
                  <div className="msd-card__subtitle">Method distribution</div>
                </div>
              </div>
              <div className="msd-card__body msd-donut">
                <div className="msd-donut__chart">
                  <CJSChart id="org-qr" build={buildQrSplit} />
                </div>
                <div className="msd-donut__legend">
                  <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#534AB7" }} />Dynamic QR<span className="msd-legend-val">{dynamicShare}%</span></div>
                  <div className="msd-legend-row"><span className="msd-dot" style={{ background: "#1D9E75" }} />Static QR<span className="msd-legend-val">{staticShare}%</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2">
            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Top Agents</div>
                </div>
              </div>
              <div className="msd-list">
                {topAgents.length === 0 ? (
                  <div className="msd-muted">No agents yet.</div>
                ) : (
                  topAgents.map((a) => (
                    <div key={a.name} className="msd-listrow">
                      <div className="msd-listrow__left">
                        <div className="msd-chip">{a.initials}</div>
                        <div>
                          <div className="msd-listrow__title">{a.name}</div>
                          <div className="msd-listrow__sub">{a.sub}</div>
                        </div>
                      </div>
                      <div className="msd-listrow__right">
                        <div className="msd-listrow__value">{a.value}</div>
                        <div className="msd-listrow__delta">{a.delta}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Top Merchants</div>
                </div>
              </div>
              <div className="msd-list">
                {topMerchants.length === 0 ? (
                  <div className="msd-muted">No merchants yet.</div>
                ) : (
                  topMerchants.map((m) => (
                    <div key={m.name} className="msd-listrow">
                      <div className="msd-listrow__left">
                        <div className="msd-chip msd-chip--dark" />
                        <div>
                          <div className="msd-listrow__title">{m.name}</div>
                          <div className="msd-listrow__sub">{m.sub}</div>
                        </div>
                      </div>
                      <div className="msd-listrow__right">
                        <div className="msd-listrow__value">{m.value}</div>
                        <div className="msd-listrow__sub">{m.foot}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Recent Transactions</div>
                </div>
                <button type="button" className="msd-linkbtn">View All</button>
              </div>
              <div className="msd-txntable">
                <div className="msd-txntable__head">
                  <div>Timestamp</div>
                  <div>Merchant</div>
                  <div>Method</div>
                  <div>Amount</div>
                  <div>Status</div>
                </div>
                {recentTx.length === 0 ? (
                  <div className="msd-txntable__empty msd-muted">No transactions.</div>
                ) : (
                  recentTx.map((t, idx) => (
                    <div key={`${t.time}-${idx}`} className="msd-txntable__row">
                      <div className="msd-mono">{t.time}</div>
                      <div className="msd-txntable__merchant">{t.merchant}</div>
                      <div className="msd-txntable__method">{t.method}</div>
                      <div className="msd-mono">{t.amount}</div>
                      <div>
                        <span className={t.status === "SUCCESS" ? "msd-pill msd-pill--success" : "msd-pill msd-pill--pending"}>
                          {t.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2 msd-admingrid__row2--bottom">
            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Approvals</div>
                </div>
                <span className="msd-badge msd-badge--review">{approvals.length} NEW</span>
              </div>
              <div className="msd-approvals">
                {approvals.length === 0 ? (
                  <div className="msd-muted">No pending approvals.</div>
                ) : (
                  approvals.map((a, idx) => (
                    <div key={`${a.name}-${idx}`} className="msd-approval">
                      <div>
                        <div className="msd-approval__title">{a.name}</div>
                        <div className="msd-approval__sub">{a.sub}</div>
                      </div>
                      <div className="msd-approval__actions">
                        <button type="button" className="msd-approvebtn">Approve</button>
                        <button type="button" className="msd-ghostbtn">Details</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Agent Activity</div>
                </div>
                
              </div>
              <div className="msd-activitylist">
                {agentActivity.length === 0 ? (
                  <div className="msd-muted">No recent activity.</div>
                ) : (
                  agentActivity.map((a, idx) => (
                    <div key={`${a.name}-${idx}`} className="msd-activityitem">
                      <span className="msd-activitydot" style={{ background: a.dot }} />
                      <div>
                        <div className="msd-activityitem__title">{a.name}</div>
                        <div className="msd-activityitem__sub">{a.sub}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="msd-admingrid__row2 msd-admingrid__row2--bottom">
            <div className="msd-card msd-card--wide">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Monthly Revenue Heatmap</div>
                </div>
              </div>
              <div className="msd-heatmap">
                {(heatmap.length ? heatmap : MONTHS.map((label) => ({ label, v: 0 }))).map((m) => (
                  <div key={m.label} className="msd-heatmap__cell">
                    <div className="msd-heatmap__box" style={{ opacity: 0.25 + m.v }} />
                    <div className="msd-heatmap__label">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="msd-card">
              <div className="msd-card__head">
                <div>
                  <div className="msd-card__title">Settlement Progress</div>
                </div>
              </div>
              <div className="msd-steps">
                {["Calculated", "Verified", "Processing", "Settled"].map((step, idx) => (
                  <div key={step} className={idx < 3 ? "msd-step active" : "msd-step"}>
                    <div className="msd-step__dot" />
                    <div className="msd-step__label">{step}</div>
                  </div>
                ))}
              </div>
              <div className="msd-nextbatch">
                <div className="msd-nextbatch__label">Next Batch</div>
                <div className="msd-nextbatch__value">{formatRs(pending)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDashboard;
